import { motion } from "framer-motion";

/**
 * Conjunto de ilustrações decorativas usadas em Empty States e banners.
 * Objetivo: nenhuma tela deve parecer "painel administrativo vazio" — mesmo
 * sem dados, a superfície deve transmitir futebol e energia (gradientes,
 * bola, taça, arquibancada), conforme guia de marca.
 */

const float = {
  animate: { y: [0, -6, 0], rotate: [0, 4, 0] },
  transition: { duration: 4, repeat: Infinity, ease: "easeInOut" as const },
};

export function StadiumIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 140" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="stadium-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E3F2FD" />
          <stop offset="100%" stopColor="#FFFFFF" />
        </linearGradient>
        <linearGradient id="stadium-pitch" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1976D2" />
          <stop offset="100%" stopColor="#0D47A1" />
        </linearGradient>
      </defs>
      <rect width="200" height="140" rx="16" fill="url(#stadium-sky)" />
      {/* arquibancadas */}
      <path d="M0 78 Q100 40 200 78 V140 H0 Z" fill="url(#stadium-pitch)" opacity="0.12" />
      <path d="M0 92 Q100 58 200 92 V140 H0 Z" fill="url(#stadium-pitch)" opacity="0.2" />
      {/* gramado */}
      <path d="M20 118 Q100 96 180 118 V132 Q100 116 20 132 Z" fill="url(#stadium-pitch)" />
      <ellipse cx="100" cy="120" rx="26" ry="7" fill="none" stroke="#FFFFFF" strokeWidth="1.4" opacity="0.7" />
      {/* holofotes */}
      <g opacity="0.9">
        <rect x="14" y="34" width="3" height="24" rx="1.5" fill="#0D47A1" />
        <circle cx="15.5" cy="32" r="6" fill="#FFC107" opacity="0.85" />
        <rect x="183" y="34" width="3" height="24" rx="1.5" fill="#0D47A1" />
        <circle cx="184.5" cy="32" r="6" fill="#FFC107" opacity="0.85" />
      </g>
      {/* bola flutuando sobre o campo */}
      <motion.g {...float}>
        <circle cx="100" cy="70" r="12" fill="#FFFFFF" stroke="#0D47A1" strokeWidth="2" />
        <path d="m100 62 4.2 3.1-1.6 5h-5.2l-1.6-5Z" fill="#0D47A1" />
      </motion.g>
    </svg>
  );
}

export function TrophyIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 140" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="trophy-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E3F2FD" />
          <stop offset="100%" stopColor="#FFFFFF" />
        </linearGradient>
        <linearGradient id="trophy-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFD84D" />
          <stop offset="100%" stopColor="#E6A800" />
        </linearGradient>
      </defs>
      <rect width="200" height="140" rx="16" fill="url(#trophy-bg)" />
      {/* raios decorativos */}
      <g stroke="#FFC107" strokeWidth="2" opacity="0.35" strokeLinecap="round">
        <line x1="100" y1="18" x2="100" y2="30" />
        <line x1="72" y1="26" x2="80" y2="35" />
        <line x1="128" y1="26" x2="120" y2="35" />
      </g>
      <motion.g {...float}>
        {/* taça */}
        <path d="M84 44h32v18c0 9-7 16-16 16s-16-7-16-16Z" fill="url(#trophy-gold)" />
        <path d="M84 48c-9 0-14 5-14 11s5 9 11 9" fill="none" stroke="#E6A800" strokeWidth="3.4" strokeLinecap="round" />
        <path d="M116 48c9 0 14 5 14 11s-5 9-11 9" fill="none" stroke="#E6A800" strokeWidth="3.4" strokeLinecap="round" />
        <rect x="96" y="78" width="8" height="10" fill="#E6A800" />
        <rect x="88" y="88" width="24" height="6" rx="2" fill="#0D47A1" />
        <rect x="82" y="94" width="36" height="7" rx="2.5" fill="#125BAA" />
        <circle cx="100" cy="53" r="6" fill="#FFF3C4" opacity="0.8" />
      </motion.g>
    </svg>
  );
}

export function GroupsIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 140" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="groups-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E3F2FD" />
          <stop offset="100%" stopColor="#FFFFFF" />
        </linearGradient>
        <linearGradient id="groups-shield" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1976D2" />
          <stop offset="100%" stopColor="#0D47A1" />
        </linearGradient>
      </defs>
      <rect width="200" height="140" rx="16" fill="url(#groups-bg)" />
      <motion.g {...float}>
        <path
          d="M100 34c10 6 20 8 28 8v22c0 20-13 33-28 40-15-7-28-20-28-40V42c8 0 18-2 28-8Z"
          fill="url(#groups-shield)"
        />
        <path d="m88 74 8 8 16-18" fill="none" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </motion.g>
      {/* avatares de participantes */}
      <circle cx="52" cy="100" r="11" fill="#FFC107" />
      <circle cx="52" cy="100" r="11" fill="none" stroke="#FFFFFF" strokeWidth="2.5" />
      <circle cx="150" cy="100" r="11" fill="#1976D2" />
      <circle cx="150" cy="100" r="11" fill="none" stroke="#FFFFFF" strokeWidth="2.5" />
      <circle cx="70" cy="112" r="8" fill="#0D47A1" opacity="0.7" />
      <circle cx="132" cy="112" r="8" fill="#FFD84D" opacity="0.85" />
    </svg>
  );
}

export function WhistleConfettiIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 140" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="confetti-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E3F2FD" />
          <stop offset="100%" stopColor="#FFFFFF" />
        </linearGradient>
      </defs>
      <rect width="200" height="140" rx="16" fill="url(#confetti-bg)" />
      <g opacity="0.8">
        <rect x="30" y="30" width="6" height="6" rx="1.5" fill="#FFC107" transform="rotate(20 33 33)" />
        <rect x="160" y="40" width="6" height="6" rx="1.5" fill="#1976D2" transform="rotate(-15 163 43)" />
        <circle cx="50" cy="60" r="3.5" fill="#0D47A1" />
        <circle cx="150" cy="70" r="3.5" fill="#FFD84D" />
        <rect x="170" y="90" width="5" height="5" rx="1.5" fill="#0D47A1" transform="rotate(35 172 92)" />
        <rect x="24" y="90" width="5" height="5" rx="1.5" fill="#1976D2" transform="rotate(-25 26 92)" />
      </g>
      <motion.g {...float}>
        <circle cx="100" cy="72" r="16" fill="#FFFFFF" stroke="#0D47A1" strokeWidth="2.2" />
        <path d="m100 60 5.6 4.1-2.1 6.7h-7l-2.1-6.7Z" fill="#0D47A1" />
      </motion.g>
    </svg>
  );
}
